// packages/api/src/iap/IapApi.ts

import { LoadEmbeddedAssetsResponse } from '../assetLibrary/RpcAssetLibraryApi'
import { RundotApiError } from "../errors"

/** the different subscription tiers RUN offers. LITE is lowest, ULTIMATE is highest */
export type SubscriptionTier = 'LITE' | 'CORE' | 'PLUS' | 'PRIME' | 'ULTIMATE';
/** the different billing intervals RUN offers for subscriptions */
export type SubscriptionInterval = 'weekly' | 'monthly' | 'annual';

/**
 * Billing intervals actually offered per subscription tier. Every tier ships
 * weekly; LITE is weekly-only, the rest add monthly + annual. Keep in sync with
 * the RevenueCat offerings — a tier must not advertise an interval it has no
 * package for.
 */
export const TIER_SUPPORTED_INTERVALS: Record<SubscriptionTier, readonly SubscriptionInterval[]> = {
  LITE: ['weekly'],
  CORE: ['weekly', 'monthly', 'annual'],
  PLUS: ['weekly', 'monthly', 'annual'],
  PRIME: ['weekly', 'monthly', 'annual'],
  ULTIMATE: ['weekly', 'monthly', 'annual'],
};

/**
 * Rejects a purchase up front when `interval` is not offered for `tier` (e.g.
 * LITE only ships weekly), throwing a {@link RundotApiError} with the
 * `UNSUPPORTED_SUBSCRIPTION_INTERVAL` code. Called at the top of every
 * `purchaseSubscription` implementation so the game gets a clear rejection
 * instead of an opaque failed checkout.
 */
export function assertTierSupportsInterval(
  tier: SubscriptionTier,
  interval: SubscriptionInterval,
): void {
  const supported = TIER_SUPPORTED_INTERVALS[tier];
  if (!supported?.includes(interval)) {
    throw new RundotApiError(
      'UNSUPPORTED_SUBSCRIPTION_INTERVAL',
      `The ${tier} subscription tier does not offer a ${interval} interval. ` +
        `Available interval(s): ${supported?.join(', ') || 'none'}.`,
    );
  }
}

export interface IsPlayerSubscribedRequest {
  tier: SubscriptionTier;
}

export interface RunSubscription {
  /** currency code for the subscription price */
  currencyCode: string;
  /** the billing interval for this subscription. e.g 'weekly' will charge the buyer weekly */
  interval: SubscriptionInterval;
  /** the price in the currency set in currencyCode */
  price: number;
  /** the product description */
  description: string;
}

export interface RunSubscriptionsResponse {
  [tier: string]: RunSubscription[];
}

export interface PurchaseSubscriptionRequest {
  tier: SubscriptionTier;
  interval: SubscriptionInterval; 
}

export interface GetSubscriptionsForTierRequest {
  tier?: SubscriptionTier; 
}

export interface PurchaseSubscriptionResponse {
  success: boolean;
}

export interface SpendCurrencyOptions {
  /** Screen/route name where the purchase occurred. Used for analytics only. */
  screenName?: string
  /**
   * Short human-readable description of what is being purchased
   * (e.g. "Unlock level 5", "Continue run"). The host renders this in the
   * spend-confirmation dialog so the player sees what they are paying for.
   * Sanitized and truncated by the host before display.
   */
  description?: string
  /**
   * Optional profile id of a UGC creator the spend should be attributed to
   * for revenue share. Forwarded to the server for future revshare bookkeeping.
   */
  beneficiaryId?: string
  /**
   * Optional story-level UGC entry id the spend buys (e.g. a RUN.tv episode
   * unlock). When set, the server keys the Recombee purchase signal to that
   * content item instead of the host game, so the recommender learns the
   * specific story rather than the whole app.
   */
  contentEntryId?: string
}

export interface SpendCurrencyResult {
  success: boolean
  /**
   * Optional failure reason. Defined sentinel values:
   *
   * - `'USER_CANCELLED'` — the player declined the host confirmation dialog
   *   before the spend was sent to the server. No currency was deducted.
   *
   * Other values are server-side error messages and are not stable.
   */
  error?: string
}

export interface OpenStoreResult {
  /** Whether a purchase was completed during the store session */
  purchased: boolean
  /** The updated balance after the store closed (saves a refetch) */
  newBalance: number
}

export type DirectPurchaseGrantKind = 'consumable' | 'durable'

export type DirectPurchaseStorePlatform = 'web' | 'ios' | 'android' | 'steam' | 'telegram'

export interface DirectPurchaseStorePrice {
  platform: DirectPurchaseStorePlatform
  displayPrice?: string
  appStoreProductId?: string
  playProductId?: string
  stripePriceIds?: Record<string, string>
  taxBehavior?: 'inclusive' | 'exclusive'
  amountByCurrency?: Record<string, number>
  stars?: number
}

export interface DirectPurchaseSku {
  sku: string
  gameId: string
  tierId: string
  nominalUsd: number
  storePrices: DirectPurchaseStorePrice[]
  grant: {
    entitlementId: string
    quantity: number
    kind: DirectPurchaseGrantKind
  }
}

/**
 * Treatment of the RUN Bits mark. `mono` is pure white with the shape in alpha,
 * so it takes a tint cleanly; `gradient` is the filled brand chip. Different
 * artwork, not one silhouette recoloured — they don't share a bounding box.
 */
export type CurrencyIconVariant = 'gradient' | 'mono'

/**
 * Artwork tier, drawn per display range (`xs` <=16px, `sm` 16-24, `md` 24-48,
 * `detail` >=48). Canvas is 4x the tier's top size, so consumers only downscale.
 * `detail` gradient is ~80KB pre-base64 — request it only when drawing large.
 */
export type CurrencyIconSize = 'xs' | 'sm' | 'md' | 'detail'

export interface GetCurrencyIconOptions {
  /** Defaults to `'gradient'`. */
  variant?: CurrencyIconVariant
  /** Defaults to `'md'`. */
  size?: CurrencyIconSize
}

export interface IapApi {
  getHardCurrencyBalance(): Promise<number>
  spendCurrency(
    productId: string,
    amount: number,
    options?: SpendCurrencyOptions,
  ): Promise<SpendCurrencyResult>
  openStore(): Promise<OpenStoreResult>
  /**
   * The RUN Bits mark as raw base64 PNG (`'data:image/png;base64,' + base64Data`).
   * No arguments returns `gradient` `md`, as it always has. Hosts predating
   * options ignore them and return that default, so `mono` degrades rather than
   * failing.
   */
  getCurrencyIcon(options?: GetCurrencyIconOptions): Promise<LoadEmbeddedAssetsResponse>
  getSubscriptions(tier?: SubscriptionTier): Promise<RunSubscriptionsResponse>
  purchaseSubscription(tier: SubscriptionTier, interval: SubscriptionInterval): Promise<PurchaseSubscriptionResponse>
  isUserSubscribed(tier: SubscriptionTier): Promise<boolean>
  hasUserMadePurchase(): Promise<boolean>
  listDirectPurchaseSkus(gameId: string): Promise<DirectPurchaseSku[]>
}
